import type { ApolloClient } from "@apollo/client";
import type { MarlinStore } from "@observe-tech/marlin-store/core";
import {
  createStore,
  SHELL_API_VERSION,
  type Permission,
  type ShellHandle,
  type User,
} from "@observe-tech/mfe-shell-api";
import type { OfflineSnapshot } from "@observe-tech/utils";
import { createAuthApi } from "./auth";
import { createSitesApi } from "./sites";
import { createPermissionsApi } from "./permissions";
import { createCagesApi } from "./cages";
import { createNavApi } from "./nav";
import { createI18nApi } from "./i18n";
import { createNotify } from "./notify";
import { createCloudSitesApi, type CloudSitesApi } from "./cloudSites";
import { createDashboardsApi, type DashboardsApi } from "./dashboards";
import { createDateRangeApi } from "./dateRange";
import { createPageMetaApi } from "./pageMeta";
import { createMfeErrorApi, type MfeErrorApi } from "./mfeError";
import { createTopbarSlotsApi } from "./topbarSlots";
import {
  isSnapshotFresh,
  isSnapshotUsable,
  offlineModeEnabled,
  readSnapshot,
  writeSnapshot,
} from "./offlineSession";
import { isAuthError, loadBootSnapshot } from "./offlineBoot";
import { clearOfflineMarker, setOfflineMarker } from "./offlineMarker";
import { emergencyPermissions } from "../exported/shared/utils/emergencySession";
import { createCloudConfigurationAction } from "../shared/cloudConfiguration";

const DEFAULT_REFRESH_VALIDITY_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
const DEGRADED_RETRY_MS = 2 * 60 * 1000;

// Cognito never tells a client how long the refresh token lives, so the offline
// budget has to be told to us. The env tracks the pool's RefreshTokenValidity in
// clients.tf; the default is that file's current value, so an unset env is
// merely stale rather than wrong.
function refreshTokenValidityMs(): number {
  const days = Number(import.meta.env.VITE_COGNITO_REFRESH_VALIDITY_DAYS);
  const valid = Number.isFinite(days) && days > 0;
  return (valid ? days : DEFAULT_REFRESH_VALIDITY_DAYS) * DAY_MS;
}

// Deliberately only what an offline boot needs to rebuild a session: identity,
// permissions and the site ids those permissions already carry. No cloudSites,
// no dashboards — offline mode restores access, it does not serve cloud data
// from a cache. Typed, so a later addition to the contract cannot slip into
// this literal unnoticed.
//
// The two timestamps answer different questions and must not be collapsed:
// savedAt is when this file was written and moves on every boot, because
// staleness of the cached permissions is measured from it. authTimeMs is when
// the user signed in, so the expiry is the same number on every boot of one
// session — the offline window cannot renew itself by relaunching the app.
function offlineSnapshotFrom(
  user: User,
  permissions: Permission[],
  siteIds: number[],
  authTimeMs: number,
): OfflineSnapshot {
  return {
    cognitoSub: user.cognitoSub,
    username: user.email,
    displayName: user.name,
    permissions,
    siteIds,
    savedAt: Date.now(),
    refreshTokenExpiresAt: authTimeMs + refreshTokenValidityMs(),
  };
}

export interface ShellHandleHostDeps {
  // Whether cognito itself answers. The host needs it to tell the two cloud
  // failures apart: the whole link being down, and cognito being up while the
  // service behind it is not. Optional, and absent it reads as offline, which
  // is what every caller before the partial-outage branch assumed.
  probeOnline?(): Promise<boolean>;
}
import { createMappingsApi } from "./mappings";

export interface ShellHandleHost {
  handle: ShellHandle & {
    cloudSites: CloudSitesApi;
    dashboards: DashboardsApi;
  };
  store: MarlinStore;
  loadInitialData(): Promise<void>;
  // Re-runs the boot load once the network is back. A no-op while online and
  // while one is already in flight, so the two signals that fire it — a
  // successful token refresh and the connectivity probe flipping — cost one
  // reload between them.
  resumeOnline(): Promise<void>;
  hydrateFromSnapshot(snapshot: OfflineSnapshot): void;
  setCurrentUser(u: User | null): void;
  mfeError: MfeErrorApi;
}

export function createShellHandleHost(
  client: ApolloClient<unknown>,
  cloudClient: ApolloClient<unknown>,
  store: MarlinStore,
  deps: ShellHandleHostDeps = {},
): ShellHandleHost {
  const auth = createAuthApi();
  const userStore = createStore<User | null>(null);
  const sites = createSitesApi();
  const permissions = createPermissionsApi(client);
  const cages = createCagesApi(client);
  const nav = createNavApi();
  const i18n = createI18nApi();
  const notify = createNotify();
  const cloudSites = createCloudSitesApi(cloudClient);
  const dashboards = createDashboardsApi(cloudClient);
  const dateRange = createDateRangeApi();
  const pageMeta = createPageMetaApi();
  const topbarSlots = createTopbarSlotsApi();
  const mfeError = createMfeErrorApi();
  const offline = createStore(false);
  const offlineExpiresAt = createStore<number | null>(null);
  const offlineDegraded = createStore(false);
  const probeOnline = deps.probeOnline ?? (() => Promise.resolve(false));
  const mappings = createMappingsApi(auth);

  const handle: ShellHandle & {
    cloudSites: CloudSitesApi;
    dashboards: DashboardsApi;
  } = {
    store: store.api,
    apiVersion: SHELL_API_VERSION,
    cloudConfiguration: createCloudConfigurationAction(cloudClient, store, () =>
      auth.getToken(),
    ),
    auth: {
      getToken: () => auth.getToken(),
      onTokenRefresh: (cb) => auth.onTokenRefresh(cb),
      currentUser: userStore.store,
    },
    permissions: {
      has: permissions.has,
      list: permissions.list,
      raw: permissions.raw,
      onChange: permissions.onChange,
    },
    sites: {
      list: sites.list,
      selectedSiteId: sites.selectedSiteId,
      setSelectedSite: sites.setSelectedSite,
      refresh: sites.refresh,
      selectedSiteIds: sites.selectedSiteIds,
      setSelectedSiteIds: sites.setSelectedSiteIds,
      selectedCageIds: sites.selectedCageIds,
      setSelectedCageIds: sites.setSelectedCageIds,
    },
    dateRange: {
      range: dateRange.range,
      setRange: dateRange.setRange,
    },
    pageMeta: {
      meta: pageMeta.meta,
      set: pageMeta.set,
      clear: pageMeta.clear,
    },
    cages: {
      listForSite: cages.listForSite,
      refresh: cages.refresh,
    },
    nav,
    i18n,
    notify,
    mappings,
    topbarSlots: {
      left: topbarSlots.left,
      center: topbarSlots.center,
      setLeft: topbarSlots.setLeft,
      setCenter: topbarSlots.setCenter,
      leftClaimed: topbarSlots.leftClaimed,
      centerClaimed: topbarSlots.centerClaimed,
      setLeftClaimed: topbarSlots.setLeftClaimed,
      setCenterClaimed: topbarSlots.setCenterClaimed,
    },
    mode: "shell",
    offline: offline.store,
    offlineExpiresAt: offlineExpiresAt.store,
    offlineDegraded: offlineDegraded.store,
    cloudSites,
    dashboards,
  };

  // Everything an offline boot needs to stand a session back up, and nothing
  // the cloud would have supplied: cloudSites and dashboards stay empty, so a
  // consumer reading them offline sees "none" rather than yesterday's answer.
  function applySnapshotData(snapshot: OfflineSnapshot): void {
    permissions.hydrate(snapshot.permissions);
    sites.setFromIds(snapshot.siteIds);
    // Per field rather than all-or-nothing: an offline boot can find a live user
    // object whose email is blank, because getCurrentUser() carries
    // signInDetails only on the screen that actually signed in, and the
    // snapshot is then the only place the real username still exists. `||`, so
    // a blank string falls through the same way a missing user does.
    const live = userStore.store.get();
    userStore.set({
      id: live?.id || snapshot.cognitoSub,
      cognitoSub: live?.cognitoSub || snapshot.cognitoSub,
      email: live?.email || snapshot.username,
      name: live?.name || snapshot.displayName,
    });
  }

  function hydrateFromSnapshot(snapshot: OfflineSnapshot): void {
    applySnapshotData(snapshot);
    offlineDegraded.set(false);
    offlineExpiresAt.set(snapshot.refreshTokenExpiresAt);
    offline.set(true);
    // The other screens cannot see this store. The marker is how they learn
    // that somebody is signed in here without a cognito answer to read.
    setOfflineMarker(snapshot.cognitoSub);
  }

  // Cognito answered, so this session holds a live token and a real expiry:
  // it is not offline and must not claim to be. What it lost is the cloud
  // behind cognito, which is a different sentence for the UI to say and a
  // different set of stores for the MFEs to read — the token bridge, the
  // watcher and the route guards all stay on their online path.
  // Reached from a reconnect as well as from a boot, so the offline half has to
  // be retracted rather than assumed absent: a session that hydrated from a
  // snapshot and then got cognito back is degraded, not offline, and leaving
  // the flag and the marker behind keeps the MFEs skipping a token bridge that
  // now has a live token to carry.
  function hydrateDegraded(snapshot: OfflineSnapshot): void {
    applySnapshotData(snapshot);
    offline.set(false);
    offlineExpiresAt.set(null);
    clearOfflineMarker();
    offlineDegraded.set(true);
  }

  // The snapshot to fall back on is this user's, and only this user's. A
  // station with several enrolments and no live session cannot say which of
  // them is at the keyboard, so it falls back to none.
  async function usableSnapshotFor(
    user: User | null,
  ): Promise<OfflineSnapshot | null> {
    if (!offlineModeEnabled()) return null;
    const snapshot = user
      ? await readSnapshot(user.cognitoSub)
      : await loadBootSnapshot();
    if (!snapshot || !isSnapshotUsable(snapshot)) return null;
    return snapshot;
  }

  async function loadLocalServerIds(): Promise<number[]> {
    return Object.values(await store.sites.getAllSites())
      .map((server) => Number(server.machine_id))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  const loadInitialData = async (): Promise<void> => {
    if (auth.emergencyRole) {
      const ids = await loadLocalServerIds();
      userStore.set(await auth.loadCurrentUser());
      permissions.hydrate(emergencyPermissions(auth.emergencyRole, ids));
      sites.setFromIds(ids);
      return;
    }
    let user: User | null = null;
    try {
      user = await auth.loadCurrentUser();
    } catch {
      user = null;
    }
    userStore.set(user);
    void mappings.refresh();
    try {
      await permissions.refresh();
    } catch (error) {
      // The permission query is the first cloud call of the boot, so this is
      // where a dead network is discovered. Rethrowing here is today's
      // behaviour and stays the behaviour whenever there is nothing cached to
      // run on.
      const snapshot = await usableSnapshotFor(user);
      if (!snapshot) throw error;
      // A reachable cognito makes this a partial outage rather than an
      // offline boot, and the budget for one is much shorter: 24 hours of
      // cached permissions is a stopgap while a service is restarted, not a
      // fortnight of running the site on a stale answer nobody re-checked.
      if (await probeOnline()) {
        if (!isSnapshotFresh(snapshot)) throw error;
        console.warn(
          "[shell-handle] cognito is up and the cloud is not; running degraded",
          error,
        );
        hydrateDegraded(snapshot);
        return;
      }
      console.warn(
        "[shell-handle] cloud unreachable; running on the offline snapshot",
        error,
      );
      hydrateFromSnapshot(snapshot);
      return;
    }
    // Deduplicated here rather than inside setFromIds alone, so the ids the
    // snapshot records are the ids the online session actually ran with.
    const ids = [...new Set(permissions.raw().flatMap((p) => p.sites))];
    sites.setFromIds(ids);
    // Neither of these rejects: each warns and empties its own store when the
    // query fails. Catching here to mark the session offline would be wrong
    // even if they did — the flag makes MFEs skip the token bridge, which is
    // only ever correct when there is no live session, and a gql error is not
    // a dead network.
    await cloudSites.refresh();
    const firstSite = sites.selectedSiteId.get();
    if (firstSite) await dashboards.refresh(Number(firstSite));
    // Permissions answered, so this session reached the cloud and whatever
    // marked it offline is no longer true. A reconnect leaves through here,
    // never through the connectivity event on its own: the flag is a claim
    // about the data the MFEs are about to read, and only a load that reached
    // the cloud can retract it.
    offline.set(false);
    offlineExpiresAt.set(null);
    offlineDegraded.set(false);
    clearOfflineMarker();
    // Last, and only on the path where every call above returned: a snapshot
    // is a claim that this identity and these permissions were true online,
    // so a partial load must not be written as one. Not awaited, because a
    // login must not wait on IPC, and a write failure is logged and dropped:
    // a machine with no keyring still logs in online, it simply gets no
    // offline mode.
    if (!user || !offlineModeEnabled()) {
      return;
    }
    const authTimeMs = await auth.getAuthTimeMs();
    // No stamp, no snapshot. The alternative is an expiry anchored on the
    // clock of whichever boot happened to write it, and an offline window
    // that renews itself indefinitely is worse than no offline window.
    if (authTimeMs === null) {
      console.warn(
        "[shell-handle] no auth_time on the id token; skipping the offline snapshot",
      );
      return;
    }
    writeSnapshot(
      offlineSnapshotFrom(user, permissions.raw(), ids, authTimeMs),
    ).catch((error: unknown) => {
      console.warn("[shell-handle] offline snapshot write failed", error);
    });
  };

  // Leaving offline is entering it run backwards, and it is the same code:
  // only a load where every cloud call answered clears the flag. Retrying the
  // load is what refills cloudSites and dashboards, so clearing the flag
  // without it would leave MFEs reading "online" off empty stores.
  // Tokens before data: the permission query is authenticated and a snapshot
  // session holds nothing live to send it. The refresh is also the first
  // moment a revoked user stops looking like a dead link — offline the two are
  // the same failure, and cognito is the only thing that can separate them, so
  // a refusal here ends the session rather than re-arming the probe.
  async function upgradeSession(): Promise<void> {
    try {
      await auth.getToken({ forceRefresh: true });
    } catch (error) {
      if (isAuthError(error)) {
        console.warn(
          "[shell-handle] the offline session was refused on reconnect",
          error,
        );
        auth.endOfflineSession();
        return;
      }
      // What a refusal actually looks like: amplify resolves with no tokens,
      // so getToken throws its own "No session". The tokenRefresh_failure it
      // dispatched first has already started the teardown.
      if (error instanceof Error && error.message === "No session") {
        console.warn(
          "[shell-handle] the offline session was refused on reconnect",
        );
        return;
      }
      console.warn(
        "[shell-handle] resume failed; staying on the offline session",
        error,
      );
      return;
    }
    try {
      await loadInitialData();
    } catch (error) {
      // A resume that fails changes nothing: the session keeps running on
      // the snapshot it already has, and the next signal tries again.
      console.warn(
        "[shell-handle] resume failed; staying on the offline session",
        error,
      );
    }
  }

  let resuming: Promise<void> | null = null;
  function resumeOnline(): Promise<void> {
    // Degraded resumes through here too. It is the only way back: nothing else
    // re-runs the boot load, so gating on `offline` alone left a session that
    // lost the cloud behind a reachable cognito running on cached permissions
    // for the life of the renderer — past the 24h freshness budget that branch
    // exists to enforce, because that budget is only ever checked here.
    if (!offline.store.get() && !offlineDegraded.store.get()) {
      return Promise.resolve();
    }
    resuming ??= upgradeSession().finally(() => {
      resuming = null;
    });
    return resuming;
  }

  // A degraded session holds a live token, so neither resume signal fires for
  // it: the connectivity poll only runs while cognito is unreachable, and the
  // token refresh only comes near expiry — up to an hour after the cloud is
  // back. The tick asks the one call that failed and nothing else, so a tick
  // that fails again costs a single request and touches no store; only an
  // answer pays for the full reload.
  let degradedRetry: ReturnType<typeof setInterval> | null = null;
  function retryDegraded(): void {
    if (resuming) return;
    resuming = permissions
      .refresh()
      .then(() => loadInitialData())
      .catch(() => {})
      .finally(() => {
        resuming = null;
      });
  }
  offlineDegraded.store.subscribe((degraded) => {
    if (degraded && !degradedRetry) {
      degradedRetry = setInterval(retryDegraded, DEGRADED_RETRY_MS);
    } else if (!degraded && degradedRetry) {
      clearInterval(degradedRetry);
      degradedRetry = null;
    }
  });

  return {
    handle,
    store,
    mfeError,
    hydrateFromSnapshot,
    setCurrentUser: (u) => userStore.set(u),
    loadInitialData,
    resumeOnline,
  };
}
